/**
 * ServiceTokensPage.js (PR_RUN_047)
 * ==================================
 * Admin-only UI for minting / listing / revoking service-account Bearer
 * tokens used by GitHub Actions crons (MK-004 trial-cron.yml) and other
 * non-interactive callers.
 *
 * Mint flow: button → POST /api/auth/admin-service-tokens → opens a modal
 * showing the plaintext ONCE with a click-to-copy button. The Copy button
 * uses navigator.clipboard.writeText() inside a user-gesture handler (the
 * click) which bypasses the "Document is not focused" restriction that
 * broke the DevTools console workflow.
 *
 * Closes the UX gap exposed during PR_RUN_046 cron secrets setup, where
 * Charles had to paste console scripts and accidentally exposed the
 * plaintext in chat twice (both tokens were revoked).
 *
 * Access: admin (mint + revoke) + auditor (list only).
 *
 * Authored: 2026-05-26 (E1, PR_RUN_047)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import QMSLayout from "../components/QMSLayout";
import { API, useAuth } from "../App";
import {
  Key, Plus, Copy, Check, Trash2, AlertTriangle, RefreshCw, Shield, X, Eye, EyeOff,
  ArrowUpDown, ArrowUp, ArrowDown, RotateCw,
} from "lucide-react";

const SCOPES = [
  { value: "trial_cron",     label: "Trial cron (GitHub Actions MK-004)" },
  { value: "digest_cron",    label: "Digest cron" },
  { value: "admin_readonly", label: "Admin readonly" },
  { value: "custom",         label: "Custom" },
];

const StatusPill = ({ revoked, expiresAt }) => {
  if (revoked) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20">
        <X className="w-3 h-3" /> Revoked
      </span>
    );
  }
  const isExpired = expiresAt && new Date(expiresAt) < new Date();
  if (isExpired) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
        Expired
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
      <Check className="w-3 h-3" /> Active
    </span>
  );
};

const MintModal = ({ token, tokenId, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback — select text in the input so the user can manually Cmd+C
      const inp = document.getElementById("svct-plaintext-input");
      inp?.select();
    }
  }, [token]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="service-token-mint-modal"
    >
      <div className="bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 max-w-2xl w-full mx-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#C9A84C]" /> New service token minted
            </h2>
            <p className="text-[11px] text-[#8B95A5] mt-1">
              Token ID: <code className="text-[#C9A84C]">{tokenId}</code> · This plaintext is shown <strong>once</strong> and cannot be retrieved again.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#8B95A5] hover:text-white"
            data-testid="service-token-mint-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 flex gap-2 text-[12px] text-amber-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Treat this string like a password. Paste it into your GitHub <code>TRIAL_CRON_ADMIN_TOKEN</code> secret (or your password manager), then close this dialog. Never paste it into chat, email, or screenshots.
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[11px] uppercase tracking-widest text-[#8B95A5]">
            Plaintext token
          </label>
          <div className="flex items-center gap-2">
            <input
              id="svct-plaintext-input"
              type={revealed ? "text" : "password"}
              value={token}
              readOnly
              className="flex-1 px-3 py-2 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] font-mono text-[12px] text-white"
              data-testid="service-token-plaintext-input"
            />
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              className="px-3 py-2 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] text-[#8B95A5] hover:text-white"
              data-testid="service-token-reveal-toggle"
              title={revealed ? "Hide token" : "Reveal token"}
            >
              {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className={`px-4 py-2 rounded text-[12px] font-medium flex items-center gap-1.5 transition-colors ${
                copied
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                  : "bg-[#C9A84C] text-[#0D0F14] hover:bg-[#D6B663]"
              }`}
              data-testid="service-token-copy-btn"
            >
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
            </button>
          </div>
          <p className="text-[10px] text-[#8B95A5]">
            Length: {token.length} chars · Should start with <code>chst_svc_</code>
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded text-[12px] text-[#8B95A5] border border-[rgba(255,255,255,0.06)] hover:text-white"
            data-testid="service-token-done-btn"
          >
            I've saved it — close
          </button>
        </div>
      </div>
    </div>
  );
};

const ServiceTokensPage = () => {
  const { isAdmin, user } = useAuth();
  const isAuditor = user?.role === "auditor";
  const canAccess = isAdmin || isAuditor;

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // mint form
  const [mintScope, setMintScope] = useState("trial_cron");
  const [mintDescription, setMintDescription] = useState("GitHub Actions cron - MK-004 trial-cron.yml");
  const [mintExpiresDays, setMintExpiresDays] = useState("");
  const [minting, setMinting] = useState(false);
  const [mintResult, setMintResult] = useState(null);

  // PR_RUN_050 — sortable "Last used" column.
  // sortKey is one of "last_used" | "created" | null (default null = API order).
  // sortDir: "desc" newest-first | "asc" oldest-first.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  // PR_RUN_050 — rotate state.
  // Rotate = revoke this token + mint a fresh one carrying the same scope
  // and description, then surface the new plaintext in the same mint modal.
  // Atomically-ish: we mint first, then revoke, so a failure mid-rotate
  // leaves both old + new alive rather than locking the caller out.
  const [rotatingId, setRotatingId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/auth/admin-service-tokens`, { credentials: "include" });
      if (!r.ok) {
        setError(`Failed to load tokens: HTTP ${r.status}`);
        return;
      }
      const j = await r.json();
      setTokens(j.items || []);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAccess) fetchList();
  }, [canAccess, fetchList]);

  const handleMint = async () => {
    setMinting(true);
    setError(null);
    try {
      const body = {
        scope: mintScope,
        description: mintDescription.trim(),
      };
      const dayInt = parseInt(mintExpiresDays, 10);
      if (mintExpiresDays && !isNaN(dayInt) && dayInt > 0) {
        body.expires_in_days = dayInt;
      }
      const r = await fetch(`${API}/auth/admin-service-tokens`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) {
        setError(j.detail || `HTTP ${r.status}`);
        return;
      }
      setMintResult({ token: j.token, token_id: j.token_id });
      await fetchList();
    } catch (e) {
      setError(String(e));
    } finally {
      setMinting(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Revoke token ${tokenId}? This is immediate and irreversible.`)) return;
    try {
      const r = await fetch(`${API}/auth/admin-service-tokens/${tokenId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) {
        setError(`Revoke failed: HTTP ${r.status}`);
        return;
      }
      await fetchList();
    } catch (e) {
      setError(String(e));
    }
  };

  // PR_RUN_050 — rotate = mint fresh + revoke old, preserving scope/desc.
  // Mint-first so a transient network failure mid-rotate doesn't leave
  // the caller locked out. The new plaintext surfaces in the same modal
  // as a fresh mint — the admin copies it into the GHA secret to replace
  // the old one, then the rotation is complete.
  const handleRotate = async (token) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Rotate token ${token.token_id}?\n\n` +
      `This will mint a new ${token.scope} token (you'll need to paste it into your secret store) ` +
      `and revoke the old one. The new plaintext is shown ONCE.`,
    )) return;
    setRotatingId(token.token_id);
    setError(null);
    try {
      // 1. Mint the replacement with the same scope + description + expiry.
      const body = {
        scope: token.scope,
        description: `${token.description || ""}${token.description ? " · " : ""}rotated from ${token.token_id}`,
      };
      // Carry expiry across rotations (rounded to days from today) so policy survives.
      if (token.expires_at_utc) {
        const remainingMs = new Date(token.expires_at_utc).getTime() - Date.now();
        const remainingDays = Math.max(1, Math.ceil(remainingMs / 86400000));
        body.expires_in_days = remainingDays;
      }
      const mintR = await fetch(`${API}/auth/admin-service-tokens`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const mintJ = await mintR.json();
      if (!mintR.ok) {
        setError(`Rotate failed at mint stage: ${mintJ.detail || `HTTP ${mintR.status}`}`);
        return;
      }
      // 2. Revoke the old token.
      const revokeR = await fetch(`${API}/auth/admin-service-tokens/${token.token_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!revokeR.ok) {
        // Leave both alive. The admin sees the new plaintext + an error
        // banner asking them to revoke the old token manually.
        setError(
          `Mint succeeded (new token_id=${mintJ.token_id}) but revoke of ${token.token_id} failed (HTTP ${revokeR.status}). ` +
          `Please revoke ${token.token_id} manually using the Revoke button after you save the new plaintext.`,
        );
      }
      setMintResult({ token: mintJ.token, token_id: mintJ.token_id });
      await fetchList();
    } catch (e) {
      setError(`Rotate failed: ${String(e)}`);
    } finally {
      setRotatingId(null);
    }
  };

  // PR_RUN_050 — Derived sorted tokens for table display.
  // Falls back to API order (newest-first) when sortKey is null.
  const sortedTokens = useMemo(() => {
    if (!sortKey) return tokens;
    const list = [...tokens];
    const field = sortKey === "last_used" ? "last_used_at_utc" : "created_at_utc";
    list.sort((a, b) => {
      const av = a[field] || "";
      const bv = b[field] || "";
      // Empty values (never used) sort last regardless of direction.
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return list;
  }, [tokens, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ activeKey }) => {
    if (sortKey !== activeKey) return <ArrowUpDown className="w-2.5 h-2.5 opacity-40" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-2.5 h-2.5 text-[#C9A84C]" />
      : <ArrowDown className="w-2.5 h-2.5 text-[#C9A84C]" />;
  };

  const stats = useMemo(() => {
    const total = tokens.length;
    const active = tokens.filter((t) => !t.revoked && (!t.expires_at_utc || new Date(t.expires_at_utc) > new Date())).length;
    const revoked = tokens.filter((t) => t.revoked).length;
    return { total, active, revoked };
  }, [tokens]);

  if (!canAccess) {
    return (
      <QMSLayout title="Service Tokens">
        <div className="p-8 text-center text-[#8B95A5]">
          <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-[#F87171]" />
          <p>Service tokens management is restricted to admin and auditor users.</p>
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title="Service Tokens">
      <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="service-tokens-page">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Key size={22} className="text-[#C9A84C]" />
              Service Tokens
            </h1>
            <p className="text-[12px] text-[#8B95A5] mt-1 max-w-xl">
              Long-lived Bearer tokens for non-interactive callers (GitHub Actions crons, scheduled jobs). Format: <code>chst_svc_…</code>. Plaintext is returned ONCE at mint time and never persisted in clear.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-[11px] text-[#8B95A5]">
              <div><span className="text-emerald-400 font-mono">{stats.active}</span> active</div>
              <div><span className="text-red-400 font-mono">{stats.revoked}</span> revoked</div>
              <div><span className="text-white font-mono">{stats.total}</span> total</div>
            </div>
            <button
              onClick={fetchList}
              disabled={loading}
              className="px-3 py-1.5 rounded text-[11px] text-[#8B95A5] border border-[rgba(255,255,255,0.06)] hover:text-white flex items-center gap-1.5 disabled:opacity-40"
              data-testid="service-tokens-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </header>

        {error && (
          <div className="px-4 py-3 rounded border border-red-500/30 bg-red-500/5 text-red-300 text-[12px] flex items-center gap-2" data-testid="service-tokens-error">
            <AlertTriangle className="w-3.5 h-3.5" />
            {error}
          </div>
        )}

        {/* Mint section — admin only */}
        {isAdmin && (
          <section className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#181B23] p-5" data-testid="service-tokens-mint-section">
            <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#C9A84C]" /> Mint new token
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8B95A5] block mb-1">Scope</label>
                <select
                  value={mintScope}
                  onChange={(e) => setMintScope(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] text-white text-[12px]"
                  data-testid="mint-scope"
                >
                  {SCOPES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-[#8B95A5] block mb-1">Description</label>
                <input
                  type="text"
                  value={mintDescription}
                  onChange={(e) => setMintDescription(e.target.value)}
                  placeholder="What is this token for?"
                  className="w-full px-3 py-2 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] text-white text-[12px]"
                  data-testid="mint-description"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8B95A5] block mb-1">Expires in days (blank = never)</label>
                <input
                  type="number"
                  min="1"
                  max="3650"
                  value={mintExpiresDays}
                  onChange={(e) => setMintExpiresDays(e.target.value)}
                  placeholder="e.g. 365"
                  className="w-full px-3 py-2 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] text-white text-[12px]"
                  data-testid="mint-expires-days"
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <button
                  onClick={handleMint}
                  disabled={minting}
                  className="px-4 py-2 rounded bg-[#C9A84C] text-[#0D0F14] text-[12px] font-medium hover:bg-[#D6B663] disabled:opacity-40 flex items-center gap-1.5"
                  data-testid="mint-submit-btn"
                >
                  {minting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  {minting ? "Minting…" : "Mint token"}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* List */}
        <section className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#181B23] overflow-hidden" data-testid="service-tokens-list">
          <table className="w-full text-[12px]">
            <thead className="bg-[#0D0F14] text-[#8B95A5] text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5">Token ID</th>
                <th className="text-left px-4 py-2.5">Scope</th>
                <th className="text-left px-4 py-2.5">Description</th>
                <th className="text-left px-4 py-2.5">
                  <button
                    type="button"
                    onClick={() => toggleSort("created")}
                    className="flex items-center gap-1 uppercase tracking-widest hover:text-white"
                    data-testid="service-tokens-sort-created"
                  >
                    Created <SortIcon activeKey="created" />
                  </button>
                </th>
                <th className="text-left px-4 py-2.5">
                  <button
                    type="button"
                    onClick={() => toggleSort("last_used")}
                    className="flex items-center gap-1 uppercase tracking-widest hover:text-white"
                    data-testid="service-tokens-sort-last-used"
                  >
                    Last used <SortIcon activeKey="last_used" />
                  </button>
                </th>
                <th className="text-left px-4 py-2.5">Status</th>
                {isAdmin && <th className="text-right px-4 py-2.5">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedTokens.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="text-center text-[#8B95A5] py-10">
                    {loading ? "Loading tokens…" : "No service tokens yet. Mint one above."}
                  </td>
                </tr>
              ) : sortedTokens.map((t) => (
                <tr
                  key={t.token_id}
                  className="border-t border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)]"
                  data-testid={`service-token-row-${t.token_id}`}
                >
                  <td className="px-4 py-3 font-mono text-[11px] text-white">{t.token_id}</td>
                  <td className="px-4 py-3 text-[#8B95A5]">{t.scope}</td>
                  <td className="px-4 py-3 text-white max-w-xs truncate" title={t.description}>{t.description || <span className="text-[#555]">—</span>}</td>
                  <td className="px-4 py-3 text-[#8B95A5]">{t.created_at_utc?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-[#8B95A5]">{t.last_used_at_utc?.slice(0, 16).replace("T", " ") || <span className="text-[#555]">never</span>}</td>
                  <td className="px-4 py-3"><StatusPill revoked={t.revoked} expiresAt={t.expires_at_utc} /></td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      {!t.revoked && (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleRotate(t)}
                            disabled={rotatingId === t.token_id}
                            className="px-2 py-1 rounded text-[10px] text-[#C9A84C] hover:text-white hover:bg-[#C9A84C]/10 flex items-center gap-1 disabled:opacity-40"
                            data-testid={`rotate-btn-${t.token_id}`}
                            title="Mint a replacement with the same scope, then revoke this one"
                          >
                            <RotateCw className={`w-3 h-3 ${rotatingId === t.token_id ? "animate-spin" : ""}`} />
                            {rotatingId === t.token_id ? "Rotating…" : "Rotate"}
                          </button>
                          <button
                            onClick={() => handleRevoke(t.token_id)}
                            className="px-2 py-1 rounded text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/10 flex items-center gap-1"
                            data-testid={`revoke-btn-${t.token_id}`}
                            title="Revoke this token"
                          >
                            <Trash2 className="w-3 h-3" /> Revoke
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {/* Mint result modal */}
      {mintResult && (
        <MintModal
          token={mintResult.token}
          tokenId={mintResult.token_id}
          onClose={() => setMintResult(null)}
        />
      )}
    </QMSLayout>
  );
};

export default ServiceTokensPage;
